import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'src/components/queryProvider';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { PATHS } from 'src/constants/paths';
import { queryKeys } from 'src/utils/queryKeys';

import { requestCCApi } from '../../../utils/axios-utils';
import { narrativeGenerators } from '../userActions/helper';

export const useDeleteComponent = () => {
  return useMutation({
    mutationFn: deleteComponent,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        predicate: (query) => {
          const key = query.queryKey[0];

          return key === 'components' || key === 'componentListing';
        },
      });

      // Get all current component queries
      const previousData = queryClient.getQueriesData({
        predicate: (query) => {
          const key = query.queryKey[0];

          return key === 'components' || key === 'componentListing';
        },
      });

      // Update all component list queries optimistically
      queryClient.setQueriesData(
        {
          predicate: (query) => {
            const key = query.queryKey[0];

            return key === 'components' || key === 'componentListing';
          },
        },
        (old: any) => {
          if (!old?.data) return old;

          return {
            ...old,
            data: old.data.filter((c: any) => c.id !== variables.componentId),
            totalCount: Math.max((old.totalCount || 0) - 1, 0),
          };
        },
      );

      return { previousData };
    },
    onSuccess: (_, variables) => {
      // Remove specific component details from cache
      queryClient.removeQueries({
        queryKey: ['components', 'details', String(variables.componentId)],
        exact: false,
      });

      // Invalidate all component-related queries
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey[0];

          return key === 'components' || key === 'componentListing';
        },
      });

      queryClient.invalidateQueries(queryKeys.componentTags.invalidateAll());
      queryClient.invalidateQueries(
        queryKeys.viewComponentTagWithComponentCount.invalidateAll(),
      );
    },
    onError: (err, variables, context: any) => {
      // Rollback optimistic update on error
      if (context?.previousData) {
        context.previousData.forEach(([key, data]: any) => {
          queryClient.setQueryData(key, data);
        });
      }
    },
  });
};

const deleteComponent = async ({
  componentId,
  componentName,
}: {
  componentId: number;
  componentName: string;
}) => {
  const response = await requestCCApi({
    url: `${PATHS.COMPONENTS_ID(componentId)}?${new URLSearchParams({
      narrative: (
        narrativeGenerators[COLLECTIONS.COMPONENTS][USER_ACTIONS.DELETE] as (
          compName: string,
        ) => string
      )(componentName),
    })}`,

    method: 'DELETE',
  });

  return response;
};
